import { Add, Delete } from '@mui/icons-material';
import { Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { Employee } from '../../../types/Employee';
import { UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AppLink from '../../common/AppLink/AppLink';
import AddEmployeeModal from './AddEmployeeModal';
import AccordionSection from '../../AccordionSection';

interface IEmployeeTableProps {
  employees: Employee[];
  setEmployees: (employees: Employee[]) => void;
}

export default function EmployeeTable({ employees, setEmployees }: IEmployeeTableProps) {
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { disabled } = useContext(DisableContext);

  const onAdd = (addedEmployees: Employee[]) => {
    const netNewEmployees = _.differenceBy(addedEmployees, employees, 'uuid');
    setEmployees([...employees, ...netNewEmployees]);
  };

  const handleRemove = () => {
    setEmployees(_.reject(employees, (employee) => _.includes(selectedEmployees, employee.uuid)));
    setSelectedEmployees([]);
  };

  const employeeTableRows = employees.map((employee) => {
    const handleSelectionChange = (checked: boolean) => {
      const newSelections = checked ? [...selectedEmployees, employee.uuid] : _.reject(selectedEmployees, (selectedUuid) => selectedUuid === employee.uuid);
      setSelectedEmployees(newSelections);
    };

    return (
      <TableRow key={employee.uuid} sx={{ ...listPageTableRowStyling }}>
        <TableCell>
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={_.includes(selectedEmployees, employee.uuid)}
            onChange={(event) => handleSelectionChange(event.target.checked)}
          />
        </TableCell>
        <TableCell>
          <AppLink to={`/${ROUTE_PATHS.EMPLOYEES}/${employee.uuid}`}>{employee.display_name}</AppLink>
        </TableCell>
        <TableCell>{employee.roles_values?.[0]}</TableCell>
        <TableCell>{employee.email}</TableCell>
        <TableCell>{employee.phone}</TableCell>
      </TableRow>
    );
  });

  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'end' }}>
      <ResponsiveButton startIcon={<Delete />} variant="outlined" color="warning" onClick={handleRemove} disabled={disabled}>
        Remove
      </ResponsiveButton>
      <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setModalOpen(true)} disabled={disabled}>
        Add
      </ResponsiveButton>
    </Box>
  );

  return (
    <AccordionSection title="Employees" headerElements={actionButtons} collapseId={UserSettingsCollapseKeys.EVENT_ITEM_EMPLOYEES}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{employeeTableRows}</TableBody>
        </Table>
      </TableContainer>

      <AddEmployeeModal open={modalOpen} onClose={() => setModalOpen(false)} alreadyAddedEmployees={_.map(employees, 'uuid')} onAdd={onAdd} />
    </AccordionSection>
  );
}
